import {
    Autocomplete,
    Box,
    FormControl,
    FormHelperText,
    TextField,
    createFilterOptions,
} from '@mui/material';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { digitsOnly, getPhoneValidationError } from '@/utils/phone';

interface CountryOption {
    name: string;
    code: string;
    dialCode: string;
    flag: string;
}

interface Props {
    value: string;
    selectedCountryCode: string;
    onChange: (value: string) => void;
    onCountryChange: (country: { dialCode: string; code: string }) => void;
    compUsage?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    label?: string;
    size?: 'small' | 'medium';
    /** When false, leaving the number empty isn't an error. A number that's entered is still checked */
    isRequired?: boolean;
}

/** Prefer these at the top of the list (existing product defaults). */
const PRIORITY_CODES = ['ca', 'us', 'in'];

function isoToFlag(isoCode: string): string {
    return [...isoCode.toUpperCase()]
        .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
        .join('');
}

function buildCountries(): CountryOption[] {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

    const countries = getCountries().map((isoCode) => {
        const code = isoCode.toLowerCase();
        return {
            name: displayNames.of(isoCode) ?? isoCode,
            code,
            dialCode: String(getCountryCallingCode(isoCode)),
            flag: isoToFlag(isoCode),
        };
    });

    return countries.sort((a, b) => {
        const aPriority = PRIORITY_CODES.indexOf(a.code);
        const bPriority = PRIORITY_CODES.indexOf(b.code);
        if (aPriority !== -1 || bPriority !== -1) {
            if (aPriority === -1) return 1;
            if (bPriority === -1) return -1;
            return aPriority - bPriority;
        }
        return a.name.localeCompare(b.name);
    });
}

const COUNTRIES = buildCountries();

const filterCountries = createFilterOptions<CountryOption>({
    stringify: (option) => `${option.name} ${option.code} ${option.dialCode} +${option.dialCode}`,
});

export default function CustomPhoneInput({
    value,
    selectedCountryCode,
    onChange,
    onCountryChange,
    compUsage,
    disabled = false,
    error = false,
    helperText = '',
    label,
    size = 'small',
    isRequired = true,
}: Props) {
    const [hasBlurred, setHasBlurred] = useState(false);
    const selectedCountry = COUNTRIES.find((c) => c.code === selectedCountryCode) || COUNTRIES[0];
    const nationalDigits = digitsOnly(value);

    const blurError =
        hasBlurred && (isRequired || nationalDigits)
            ? getPhoneValidationError(nationalDigits, selectedCountryCode)
            : '';
    const showError = Boolean(error || blurError);
    const displayHelperText = helperText || blurError;

    useEffect(() => {
        if (value && /[^\d]/.test(value)) {
            onChange(digitsOnly(value));
        }
    }, [value, onChange]);

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            width={compUsage === 'mpc' ? '92%' : '100%'}
        >
            <FormControl sx={{ minWidth: 128 }}>
                <Autocomplete
                    options={COUNTRIES}
                    value={selectedCountry}
                    onChange={(_event, country) => {
                        if (country) {
                            onCountryChange({
                                dialCode: country.dialCode,
                                code: country.code,
                            });
                        }
                    }}
                    disableClearable
                    disabled={disabled}
                    size={size}
                    filterOptions={filterCountries}
                    isOptionEqualToValue={(option, optionValue) => option.code === optionValue.code}
                    getOptionLabel={(option) => `${option.flag} +${option.dialCode}`}
                    renderOption={(props, option) => (
                        <li {...props} key={option.code}>
                            {option.flag} {option.name} (+{option.dialCode})
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} size={size} />}
                    componentsProps={{
                        popper: {
                            sx: { minWidth: 280 },
                        },
                    }}
                />
                {showError && <FormHelperText>&nbsp;</FormHelperText>}
            </FormControl>

            <TextField
                value={nationalDigits}
                onChange={(e) => {
                    onChange(digitsOnly(e.target.value));
                }}
                onBlur={() => setHasBlurred(true)}
                label={label}
                placeholder="Enter cell number"
                fullWidth
                size={size}
                disabled={disabled}
                error={showError}
                helperText={displayHelperText}
            />
        </Box>
    );
}
